<template>
  <div class="popup z-index-10-fg confirmation small-popup edit-interaction-popup" ref="popupWindow" @dragenter="$refs.fileUpload.dragging = true">
    <div class="popup-content" ref="popupContent">
      <div class="popup-header">
        <h2>{{ isEditable ? 'Edit' : 'View' }} Interaction</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
        <div class="paperclip-icon" @click="$refs.fileUpload.$refs.fileUploadInput.click()">
          <img id="noHover" src="../assets/paperclip.svg" alt="" />
          <img id="hover" src="../assets/paperclip-hover.svg" alt="" />
        </div>
      </div>
      <div class="popup-body">
        <Dropdown :options="interactionTypes" label="Type" v-model="type" class="input-item medium-item" :disabled="!isEditable" />

        <div class="primary-input input-item medium-item">
          <input type="date" v-model="date" :disabled="!isEditable" />
          <label>Date</label>
        </div>

        <div class="primary-input input-item large-item">
          <textarea type="text" v-model="note" rows="4" style="resize: none" :disabled="!isEditable"></textarea>
          <label>Note</label>
        </div>
        <FileUpload
          :leadid="leadid"
          :interactionid="interactionid"
          :isPanel="true"
          ref="fileUpload"
          @updateFileList="
            (e) => {
              fileList = e;
            }
          " />
        <div class="file-list" v-if="fileList.length">
          <div v-for="file in fileList">
            <img src="../assets/paperclip-thumbnail.svg" />
            <span>{{ file }}</span>
          </div>
        </div>
      </div>
      <div class="popup-files" v-if="files?.length > 0">
        <div class="popup-file" v-for="file in files">
          <span @click="openFolder(file.name)">
            <img v-if="checkFileType(file.type) == 'image'" src="../assets/filetype-image.svg" />
            <img v-else-if="checkFileType(file.type) == 'pdf'" src="../assets/filetype-pdf.svg" />
            <img v-else-if="checkFileType(file.type) == 'word'" src="../assets/filetype-word.svg" />
            <img v-else-if="checkFileType(file.type) == 'excel'" src="../assets/filetype-excel.svg" />
            <img v-else-if="checkFileType(file.type) == 'email'" src="../assets/filetype-email.svg" />
            <img v-else src="../assets/filetype-other.svg" alt="" />{{ file.name }}
          </span>
        </div>
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">close</button>
        <button v-if="isEditable" :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="saveInteraction()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import Dropdown from './inputs/Dropdown.vue';
import useAlerts from '@/composables/useAlerts';
import useLeadsQuery from '@/composables/useLeadsQuery';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import useInteractionMutate from '@/composables/useInteractionMutate';
import { computed } from 'vue';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useCurrentUser from '@/composables/useCurrentUser';
import FileUpload from './FileUpload.vue';

export default {
  name: 'Edit Interaction',
  emits: ['closePopup'],
  components: {
    Dropdown,
    FileUpload,
  },
  props: {
    interactionid: {
      type: String,
      required: true,
    },
    leadid: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      required: true,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    const { updateInteraction } = useInteractionMutate();
    const { encryptSymbols } = useDecryptSymbols();
    const { user } = useCurrentUser();

    const interactionTypes = computed(() => {
      return usePicklistItemsQuery.pickLists.interactionTypes.items.map((item) => item.NAME);
    });

    return {
      addAlert,
      leads: useLeadsQuery.leadsIncludingSingleLead,
      interactionTypes,
      updateInteraction,
      encryptSymbols,
      user,
    };
  },
  data() {
    return {
      isSaving: false,
      type: '',
      date: '',
      note: '',
      interaction: null,
      fileList: [],
    };
  },
  computed: {
    isEditable() {
      return this.user.leadsAdmin || this.interaction?.SALESPERSONID == this.user.userid;
    },
  },
  methods: {
    async saveInteraction() {
      this.isSaving = true;
      const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
      if (!lead) {
        this.addAlert('Error saving interaction', 'high', 4);
        this.$emit('closePopup');
        return;
      }

      if (new Date() < new Date(this.date)) {
        this.addAlert('Cannot add an interaction in the future', 'high', 4);
        this.isSaving = false;
        return;
      }

      const updatedInteraction = {
        NOTE: this.note,
        TYPE: this.type,
        DATE: new Date(this.date).getTime().toString(),
      };

      await this.updateInteraction(this.interactionid, this.encryptSymbols(updatedInteraction));

      const interaction = lead.interactions.find((interaction) => interaction.INTERACTIONID === this.interactionid);
      interaction.TYPE = updatedInteraction.TYPE;
      interaction.NOTE = updatedInteraction.NOTE;
      interaction.DATE = new Date(new Date(parseInt(updatedInteraction.DATE)).getTime() + 8 * 60 * 60 * 1000).getTime();

      await this.$refs.fileUpload.uploadFiles();

      this.isSaving = false;
      this.addAlert('Updated interaction', 'low', 3);
      this.$emit('closePopup');
    },
    getISODate(date) {
      return date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;
    },
    checkFileType(path) {
      const extension = path.split('.').pop().toLowerCase();
      switch (extension) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return 'image';
        case 'pdf':
          return 'pdf';
        case 'doc':
        case 'docx':
          return 'word';
        case 'xls':
        case 'xlsx':
          return 'excel';
        case 'eml':
        case 'msg':
          return 'email';
        default:
          return 'other';
      }
    },
    openFolder(file) {
      let fileUrl = '';
      if (file) {
        fileUrl = '\\' + file;
      }
      window.location.href = `openfile:\\\\192.168.16.22\\Data\\Contact Manager\\Leads\\${this.leadid}\\${this.interactionid}\\${fileUrl}`;
    },
  },
  mounted() {
    const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
    if (!lead) {
      this.addAlert('Error loading interaction', 'high', 4);
      this.$emit('closePopup');
      return;
    }
    const interaction = lead.interactions.find((interaction) => interaction.INTERACTIONID === this.interactionid);
    if (!lead) {
      this.addAlert('Error loading interaction', 'high', 4);
      this.$emit('closePopup');
      return;
    }

    this.type = interaction.TYPE;
    this.note = interaction.NOTE;
    this.interaction = interaction;
    const dateInt = parseInt(interaction.DATE);
    if (dateInt != NaN) {
      this.date = this.getISODate(new Date(dateInt));
    }
  },
};
</script>

<style lang="scss" scoped>
.edit-interaction-popup.small-popup {
  width: 30rem;

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;
    margin-bottom: 1.5rem;

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 10%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }

      &.medium-item {
        min-width: 5rem;
        flex: 1 0 25%;
      }
    }
  }
}

.popup-files {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-bottom: 1.5rem;

  span {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.3rem;
    cursor: pointer;
    font-size: $fontSize12;

    &:hover {
      text-decoration: underline;
    }

    img {
      width: 1.2rem;
    }
  }
}
</style>
